import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Navigation, Filter, Target, Zap, TrendingUp, DollarSign, Layers, Eye, EyeOff, Users } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
import { CompetitorAnalysisResponse, Competitor } from '../../types/competitor';

interface CompetitorMapProps {
  analysis: CompetitorAnalysisResponse | null;
  loading: boolean;
  onRadiusChange: (radius: number) => void;
  onCompetitorSelect: (competitor: Competitor | null) => void;
  selectedCompetitor: Competitor | null;
}

interface MapMetric {
  id: string;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}

const mapMetrics: MapMetric[] = [
  {
    id: 'price',
    name: 'Fuel Price',
    icon: DollarSign,
    description: 'Average fuel price per gallon'
  },
  {
    id: 'volume',
    name: 'Volume',
    icon: Zap,
    description: 'Estimated monthly fuel volume'
  },
  {
    id: 'distance',
    name: 'Distance',
    icon: Navigation,
    description: 'Distance from subject property'
  },
  {
    id: 'threat',
    name: 'Threat Level',
    icon: Target,
    description: 'Competitive threat assessment'
  }
];

// Global type declaration for Google Maps
// Note: Also declared in valuation/CompetitorMap.tsx - keep in sync
declare global {
  interface Window {
    google: typeof google;
  }
}

export const CompetitorMap: React.FC<CompetitorMapProps> = ({ 
  analysis, 
  loading, 
  onRadiusChange, 
  onCompetitorSelect,
  selectedCompetitor 
}) => {
  const [radius, setRadius] = useState("5");
  const [showHeatMap, setShowHeatMap] = useState(false);
  const [filterBrand, setFilterBrand] = useState("all");
  const [selectedMetric, setSelectedMetric] = useState(mapMetrics[0].id);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const heatmapRef = useRef<google.maps.visualization.HeatmapLayer | null>(null);

  const initializeMap = () => {
    if (!mapRef.current || !window.google || !analysis) return;

    const map = new google.maps.Map(mapRef.current, {
      zoom: 12,
      center: analysis.subjectProperty.coordinates,
      mapTypeControl: true,
      streetViewControl: true,
      fullscreenControl: true,
      styles: [
        {
          featureType: 'poi.business',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        }
      ]
    });

    mapInstanceRef.current = map;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    // Add subject property marker (special styling)
    const subjectMarker = new google.maps.Marker({
      position: analysis.subjectProperty.coordinates,
      map: map,
      title: analysis.subjectProperty.name,
      icon: {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
            <circle cx="16" cy="16" r="12" fill="#10B981" stroke="#ffffff" stroke-width="3"/>
            <circle cx="16" cy="16" r="6" fill="#ffffff"/>
          </svg>
        `),
        scaledSize: new google.maps.Size(32, 32),
        anchor: new google.maps.Point(16, 16)
      }
    });

    const subjectInfoWindow = new google.maps.InfoWindow({
      content: createInfoWindowContent(analysis.subjectProperty, true)
    });

    subjectMarker.addListener('click', () => {
      subjectInfoWindow.open(map, subjectMarker);
    });

    markersRef.current.push(subjectMarker);

    // Add competitor markers
    const filteredCompetitors = analysis.competitors.filter(comp => 
      filterBrand === 'all' || comp.brand.toLowerCase() === filterBrand.toLowerCase()
    );

    filteredCompetitors.forEach((competitor, index) => {
      const marker = new google.maps.Marker({
        position: competitor.coordinates,
        map: map,
        title: competitor.name,
        icon: {
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg width="28" height="28" viewBox="0 0 28 28" xmlns="http://www.w3.org/2000/svg">
              <circle cx="14" cy="14" r="10" fill="${getMarkerColor(competitor, selectedMetric)}" stroke="#ffffff" stroke-width="2"/>
              <text x="14" y="18" text-anchor="middle" fill="white" font-size="10" font-weight="bold">${index + 1}</text>
            </svg>
          `),
          scaledSize: new google.maps.Size(28, 28),
          anchor: new google.maps.Point(14, 14)
        }
      });

      const infoWindow = new google.maps.InfoWindow({
        content: createInfoWindowContent(competitor, false)
      });

      marker.addListener('click', () => {
        infoWindow.open(map, marker);
        onCompetitorSelect(competitor);
      });

      markersRef.current.push(marker);
    });

    // Add radius circle
    const radiusCircle = new google.maps.Circle({
      strokeColor: '#10B981',
      strokeOpacity: 0.3,
      strokeWeight: 2,
      fillColor: '#10B981',
      fillOpacity: 0.1,
      map: map,
      center: analysis.subjectProperty.coordinates,
      radius: parseFloat(radius) * 1609.34 // Convert miles to meters
    });

    // Fit bounds to show all markers
    const bounds = new google.maps.LatLngBounds();
    bounds.extend(analysis.subjectProperty.coordinates);
    filteredCompetitors.forEach(comp => bounds.extend(comp.coordinates));
    map.fitBounds(bounds);
  };

  const createInfoWindowContent = (property: any, isSubject: boolean) => {
    const metrics = property.currentMetrics || property.metrics;
    return `
      <div style="padding: 8px; min-width: 200px;">
        <h3 style="margin: 0 0 8px 0; color: ${isSubject ? '#10B981' : '#374151'}; font-size: 14px;">
          ${isSubject ? '🎯 ' : ''}${property.name}
        </h3>
        <p style="margin: 0 0 8px 0; font-size: 12px; color: #6B7280;">${property.address}</p>
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 4px; font-size: 11px;">
          <div><strong>Price:</strong> $${metrics.fuelPrice.toFixed(2)}</div>
          <div><strong>Volume:</strong> ${(metrics.monthlyVolume / 1000).toFixed(0)}K</div>
          ${!isSubject ? `
            <div><strong>Distance:</strong> ${property.distance.toFixed(1)} mi</div>
            <div><strong>Strength:</strong> ${(property.competitiveStrength * 100).toFixed(0)}%</div>
          ` : ''}
        </div>
      </div>
    `;
  };

  const getMarkerColor = (competitor: Competitor, metric: string) => {
    switch (metric) {
      case 'price':
        return competitor.metrics.fuelPrice > 3.5 ? '#EF4444' : competitor.metrics.fuelPrice > 3.0 ? '#F59E0B' : '#10B981';
      case 'volume':
        return competitor.metrics.monthlyVolume > 50000 ? '#10B981' : competitor.metrics.monthlyVolume > 30000 ? '#F59E0B' : '#EF4444';
      case 'distance':
        return competitor.distance < 2 ? '#EF4444' : competitor.distance < 5 ? '#F59E0B' : '#10B981';
      case 'threat':
        return competitor.competitiveStrength > 0.7 ? '#EF4444' : competitor.competitiveStrength > 0.4 ? '#F59E0B' : '#10B981';
      default:
        return '#6B7280';
    }
  };

  const setupHeatMap = () => {
    if (!mapInstanceRef.current || !analysis || !window.google.maps.visualization) return;

    if (heatmapRef.current) {
      heatmapRef.current.setMap(null);
    }

    const heatmapData = analysis.competitors.map(comp => ({
      location: new google.maps.LatLng(comp.coordinates.lat, comp.coordinates.lng),
      weight: comp.competitiveStrength * comp.marketShare * 100
    }));

    heatmapRef.current = new google.maps.visualization.HeatmapLayer({
      data: heatmapData,
      map: showHeatMap ? mapInstanceRef.current : null,
      radius: 50,
      opacity: 0.6
    });
  };

  useEffect(() => {
    if (window.google && window.google.maps) {
      initializeMap();
    } else {
      // Load Google Maps API if not already loaded
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&libraries=visualization`;
      script.onload = initializeMap;
      document.head.appendChild(script);
    }
  }, [analysis, filterBrand, radius]);

  useEffect(() => {
    setupHeatMap();
  }, [showHeatMap, analysis]);

  const handleRadiusChange = (newRadius: string) => {
    setRadius(newRadius);
    onRadiusChange(parseFloat(newRadius));
  };

  const availableBrands = analysis ? 
    ['all', ...Array.from(new Set(analysis.competitors.map(c => c.brand)))] : 
    ['all'];

  if (loading) {
    return (
      <Card className="col-span-1 lg:col-span-2">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 p-4 border-b">
          <h2 className="text-base md:text-lg font-semibold">Competitor Map</h2>
        </div>
        <div className="relative w-full" style={{ height: 'calc(100vh - 24rem)' }}>
          <div className="absolute inset-0 bg-muted animate-pulse flex items-center justify-center">
            <span className="text-sm text-muted-foreground">Loading competitor analysis...</span>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="col-span-1 lg:col-span-2">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 p-4 border-b">
        <h2 className="text-base md:text-lg font-semibold">Interactive Competitor Map</h2>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Button 
            variant={showHeatMap ? "primary" : "outline"} 
            size="sm" 
            className="w-full sm:w-auto text-sm"
            onClick={() => setShowHeatMap(!showHeatMap)}
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Heat Map
          </Button>
          <Button variant="outline" size="sm" className="w-full sm:w-auto text-sm">
            <Target className="w-4 h-4 mr-2" />
            Center Map
          </Button>
        </div>
      </div>

      <div className="relative w-full" style={{ height: 'calc(100vh - 24rem)' }}>
        <div ref={mapRef} className="w-full h-full" />
        
        {/* Map Controls Overlay */}
        <div className="absolute top-4 left-4 flex flex-col gap-2">
          <Select
            value={filterBrand}
            onChange={setFilterBrand}
            options={availableBrands.map(brand => ({
              value: brand,
              label: brand === 'all' ? 'All Brands' : brand
            }))}
            className="w-32 text-sm bg-white shadow-lg"
          />
          <Select
            value={selectedMetric}
            onChange={setSelectedMetric}
            options={mapMetrics.map(metric => ({
              value: metric.id,
              label: metric.name
            }))}
            className="w-32 text-sm bg-white shadow-lg"
          />
        </div>

        {/* Legend */}
        <div className="absolute bottom-4 left-4 bg-white p-3 rounded-lg shadow-lg text-xs">
          <div className="font-semibold mb-2">Legend</div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span>Subject Property</span>
          </div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span>High Threat (80%+)</span>
          </div>
          <div className="flex items-center gap-2 mb-1">
            <div className="w-3 h-3 rounded-full bg-orange-500"></div>
            <span>Medium Threat (60-80%)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-gray-500"></div>
            <span>Low Threat (&lt;60%)</span>
          </div>
        </div>
      </div>

      <div className="p-4 border-t">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
          <div className="text-sm text-muted-foreground">
            Showing <span className="font-medium">{analysis?.competitors.length || 0}</span> competitors within{' '}
            <span className="font-medium">{radius} miles</span>
            {analysis?.gameTheoryResults && (
              <span className="ml-2">
                • Nash Equilibrium: <span className="font-medium text-green-600">
                  ${analysis.gameTheoryResults.nashEquilibrium.optimalPricing.toFixed(2)}
                </span>
              </span>
            )}
          </div>
          <Select
            value={radius}
            onChange={handleRadiusChange}
            options={[
              { value: "1", label: "1 mile radius" },
              { value: "3", label: "3 mile radius" },
              { value: "5", label: "5 mile radius" },
              { value: "10", label: "10 mile radius" },
              { value: "15", label: "15 mile radius" }
            ]}
            className="w-full sm:w-[180px] text-sm"
          />
        </div>
      </div>
    </Card>
  );
};